import React from 'react';
import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { replaceAll } from '../../../../TCSMApp/global/helpers/string-helpers';
import { ContentSpacer } from '../../../../TCSMApp/global/components/layout/generic-content-spacer';

interface Props<TProps> {
    completeList: TProps[];
    currentPageIndex: number;
    navigateToPageIndex: (pageIndex: number) => void;
    renderListItem: (item: TProps, itemIndex: number) => JSX.Element;
    paginationCountTemplate?: string;
    emptyMessage?: string;
    pageSize?: number;
    pageNavigatorBeforeItems?: number;
    pageNavigatorAfterItems?: number;
    renderHeaders: () => JSX.Element;
    searchInProgress?: boolean;
}

interface PageRange {
    begin: number;
    end: number
}

const defaultCountTemplate = '{begin} - {end} of {total}';

export function PaginatedList<TProps>(props: React.PropsWithChildren<Props<TProps>>) {
    const { completeList, renderListItem, emptyMessage, pageSize,
        currentPageIndex, paginationCountTemplate, navigateToPageIndex,
        pageNavigatorBeforeItems, pageNavigatorAfterItems, renderHeaders: RenderHeaders,
        searchInProgress } = props;

    function GetListByPage(currentPage: number, totalCount: number): PageRange {
        return { begin: ((currentPage - 1) * pageSize), end: Math.min((currentPage * pageSize), totalCount) };
    }

    const listLength = completeList?.length;
    const totalPages = Math.ceil(listLength / pageSize);
    const range = GetListByPage(currentPageIndex, listLength);
    let filteredList = (totalPages > 1) ? completeList?.slice(range.begin, range.end) : completeList;

    // In case the last page has only one item.
    if (completeList?.length > 0 && range.begin === range.end) {
        filteredList = [];
        filteredList.push(completeList[completeList.length - 1]);
    }    

    // Navigation button visibility
    const hasNavigateToStart = currentPageIndex > 1;
    const hasNavigateToPrevious = currentPageIndex > 1;
    const hasNavigateToNext = currentPageIndex < totalPages;
    const hasNavigateToLast = currentPageIndex < totalPages;
    const hasEllipsisNext = hasNavigateToLast && currentPageIndex < totalPages - pageNavigatorAfterItems;
    const hasEllipsisPrev = hasNavigateToStart && currentPageIndex > pageNavigatorBeforeItems;


    if (!searchInProgress && (!filteredList || filteredList.length === 0)) {
        if (!emptyMessage) return null;
        
        return (
            <>
                <div>{emptyMessage}</div>
            </>
        );
    }

    function RenderItems() {
        const startingOffset = ((currentPageIndex - 1) * pageSize);

        if (searchInProgress){
            return <ContentSpacer><LoadingCircle /></ContentSpacer> ;
        }

        return (
            <>
                { filteredList.map((item, index) => renderListItem(item, startingOffset + index))}
            </>
        );
    }

    function RenderPaginationCount() {
        const paginationTemplate = paginationCountTemplate?.toLocaleLowerCase() || defaultCountTemplate;
        const replacedBegin = replaceAll(paginationTemplate, '{begin}', `${range.begin + 1}`);
        const replacedEnd = replaceAll(replacedBegin, '{end}', `${range.end}`);
        const replacedTotal = replaceAll(replacedEnd, '{total}', `${listLength}`);

        return (
            <div>
                { replacedTotal}
            </div>
        );
    }

    function RenderPageButton(pageIndex: number) {
        if (pageIndex === currentPageIndex) {
            return (
                <>
                    <span className='quickfind-pagination-page-current' key={pageIndex}> {pageIndex} </span>
                </>
            );
        }

        return (
            <>
                <button className='quickfind-pagination-page-link'  type='button' key={pageIndex} onClick={() => navigateToPageIndex(pageIndex)}> {`${pageIndex}`} </button>
            </>
        );
    }

    function RenderPaginationPageButtons() {
        const pages = [];

        pages.push(currentPageIndex);

        for (let i = 1; i < pageNavigatorBeforeItems; i += 1) {
            if (currentPageIndex - i > 0) {
                pages.unshift(currentPageIndex - i);
            }
        }

        for (let i = 1; i < pageNavigatorAfterItems; i += 1) {
            if (currentPageIndex + i <= totalPages) {
                pages.push(currentPageIndex + i);
            }
        }

        return (
            <>
                {pages.map((pageIndex) => RenderPageButton(pageIndex))}
            </>
        );
    }

    function RenderPaginationNavigationButtons() {

        if (totalPages === 1) return null;

        return (
            <div>

                { hasNavigateToStart && <button className=' quickfind-pagination-prev-next' type='button' onClick={() => navigateToPageIndex(1)}> {'<<'} </button>}
                { hasNavigateToPrevious && <button className='btn-secondary quickfind-pagination-prev-next' type='button' onClick={() => navigateToPageIndex(currentPageIndex - 1)}> {'<'} </button>}
                { hasEllipsisPrev && <span>...</span>}
                <RenderPaginationPageButtons />
                { hasEllipsisNext && <span>...</span>}
                { hasNavigateToNext && <button className='quickfind-pagination-prev-next' type='button' onClick={() => navigateToPageIndex(currentPageIndex + 1)}> {'>'} </button>}
                { hasNavigateToLast && <button className='btn-secondary quickfind-pagination-prev-next' type='button' onClick={() => navigateToPageIndex(totalPages)}> {'>>'} </button>}
            </div>
        );
    }

    function RenderPaginationButtons() {
        return (
            <>
                <RenderPaginationNavigationButtons />
            </>
        );
    }

    function RenderPagination() {
        if (searchInProgress) return null;
        
        return (
            <div className='quickfind-pagination'>
                <RenderPaginationCount />
                <RenderPaginationButtons />
            </div>
        );
    }

    return (
        <>
            <div className='quickfind-results-table'>
                <RenderHeaders />
                <div className='quickfind-results-table-content'>
                    <RenderItems />
                </div>
            </div>
            <RenderPagination />
        </>
    );
}

PaginatedList.defaultProps = {
    emptyMessage: 'No results',
    pageSize: 15,
    paginationCountTemplate: defaultCountTemplate,
    pageNavigatorBeforeItems: 3,
    pageNavigatorAfterItems: 3,
    searchInProgress: false,
};